import React, {useEffect, useState, Suspense, lazy} from 'react';
import {BrowserRouter as Router, Route, Routes, Navigate} from 'react-router-dom';
import axios from 'axios';
import authStorage from './utils/authStorage';
import Search from './components/search';
import TopBar from './components/topbar';
import Login from './components/pages/login/login';
import Holdings from "./components/holdings";
import Policy from './components/pages/policy/policy';
import ResetPasswordPage from './components/pages/login/ResetPassword';
import ActivationPage from './components/pages/login/ActivationPage.js';

const SignUp = lazy(() => import('./components/pages/signup/signup'));

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [activeComponent, setActiveComponent] = useState('search');
    const [contactOpen, setContactOpen] = useState(false);
    const [faqOpen, setFaqOpen] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);

    useEffect(() => {
        const token = authStorage.getItem('accessToken');
        if (token) {
            setIsLoggedIn(true);
        }
    }, [isLoggedIn]);

    const handleLogin = () => {
        setIsLoggedIn(true);
        setActiveComponent('search');
    };

    const handleLogout = async () => {
        try {
            const accessToken = authStorage.getItem('accessToken');
            if (accessToken) {
                await axios.post('/accounts/logout/', {}, {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    }
                });
            }
        } catch (err) {
            console.log('Error logging out on server:', err);
        } finally {
            // ALWAYS clear local storage and reset state
            authStorage.clear();
            setActiveComponent('login');
            setIsLoggedIn(false);
            console.log('Local session cleared.');
        }
    };

    useEffect(() => {
        console.log('THE OPEN - ', faqOpen, contactOpen)
    }, [faqOpen, contactOpen])

    // Heartbeat: send every 30s to keep session alive for single-session enforcement
    useEffect(() => {
        if (!isLoggedIn) return;

        const sendHeartbeat = async () => {
            try {
                const accessToken = authStorage.getItem('accessToken');
                const sessionToken = authStorage.getItem('sessionToken');
                if (!accessToken || !sessionToken) return;

                await axios.post('/accounts/heartbeat/', 
                    { session_token: sessionToken },
                    { headers: { Authorization: `Bearer ${accessToken}` } }
                );
            } catch (error) {
                console.log('Heartbeat error:', error);
            }
        };

        // Send immediately on login, then every 30 seconds
        sendHeartbeat();
        const interval = setInterval(sendHeartbeat, 30000);

        return () => clearInterval(interval);
    }, [isLoggedIn]);

    // UNCOMMENT THE IF BLOCK FOR PRODUCTION FOR NOW
    // COMMENT IN DEVELOPMENT
    // if (isMobile && isLoggedIn) {
    //   handleLogout();
    // }
    
    useEffect(() => {
        const handleCheckMobile = () => {
            setIsMobile(window.innerWidth < 1024); // Adjust the width as needed
        };
        handleCheckMobile();
        window.addEventListener('resize', handleCheckMobile);
        
        return () => window.removeEventListener('resize', handleCheckMobile);
    }, [isMobile]);
    
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPWABtn, setShowPWABtn] = useState(false);
    
    useEffect(() => {
        const handler = (e) => {
          e.preventDefault(); // Prevent the default mini-infobar
        //   console.log('THE PWA - ', e)
          setDeferredPrompt(e);
          setShowPWABtn(true); // Show the custom install button
        };
    
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
      }, []);


      
    
    return (
        <Router>
            <div className="App">
                {isLoggedIn && (
                <TopBar
                    isLoggedIn={isLoggedIn}
                    onContactClick={() => setContactOpen(!contactOpen)}
                    onAlertClick={() => setAlertOpen(!alertOpen)}
                    onFaqClick={() => setFaqOpen(!faqOpen)}
                    onSearchClick={() => setActiveComponent('search')}
                    onLoginClick={() => setActiveComponent('login')}
                    onLogoutClick={handleLogout}
                    deferredPrompt={deferredPrompt}
                    setDeferredPrompt={setDeferredPrompt}
                    showPWABtn={showPWABtn}
                    setShowPWABtn={setShowPWABtn}
                />
                )}
                <main className={isLoggedIn ? "main grid grid-cols-1 grid-rows-[80px, auto] lg:grid-rows-[30px,80px,auto]" : ""} style={!isLoggedIn ? { overflow: 'visible', minHeight: '100vh', backgroundColor: '#f8fafc' } : undefined}>
                    <Routes>
                        <Route
                            path="/policy"
                            element={<Policy />}
                        />
                        <Route
                            path="/reset-password/:uidb64/:token"
                            element={<ResetPasswordPage />}
                        />
                        <Route
                            path="/activate/:uidb64/:token"
                            element={<ActivationPage />}
                        />

                        <Route
                            path="/"
                            element={isLoggedIn ? (
                                <>
                                    <Holdings/>
                                    <Search 
                                        isMobile={isMobile}
                                        contactOpen={contactOpen} 
                                        faqOpen={faqOpen} 
                                        setContactOpen={setContactOpen}
                                        setFaqOpen={setFaqOpen}
                                        alertOpen={alertOpen}
                                        setAlertOpen={setAlertOpen}
                                        handle_logout={handleLogout}
                                    />
                                </>
                            ) : (
                                <Navigate to="/login"/>
                            )}
                        />

                        {/*<Route*/}
                        {/*    path="/"*/}
                        {/*    element={(*/}
                        {/*        <>*/}
                        {/*            <Holdings/>*/}
                        {/*            <Search/>*/}
                        {/*        </>*/}
                        {/*    )}*/}
                        {/*/>*/}

                        <Route
                            path="/login"
                            element={
                                !isLoggedIn ? (
                                    <Login handleLogin={handleLogin}/>
                                ) : (
                                    <Navigate to="/"/>
                                )}
                        />
                        
                        <Route
                            path="/signup"
                            element={
                                !isLoggedIn ? (
                                    <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
                                        <SignUp handleLogin={handleLogin}/>
                                    </Suspense>
                                ) : (
                                    <Navigate to="/search"/>
                                )}
                        />
                        <Route
                            path="*"
                            element={<Navigate to="/login"/>}
                        />
                    </Routes>
                </main>
            </div>
        </Router>
    );
    
}


export default App;
